import React, { useState } from "react";
import { Megaphone, Sparkles, Send, Check, Upload, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/seo/SEO";

const OPPORTUNITY_OPTIONS = [
  "YCN App Commercial",
  "Pre-Roll Video Advertisement",
  "Mid-Roll Video Advertisement",
  "Post-Roll Video Advertisement",
  "Sponsored Show Segment",
  "Product Placement or Brand Integration",
  "Website Advertising",
  "Social Media Promotion",
  "Event Sponsorship",
  "Featured Business Spotlight",
  "Custom Partnership",
  "Not Sure — I Would Like Recommendations",
];

const BUDGET_OPTIONS = [
  "Under $500",
  "$500–$1,000",
  "$1,000–$2,500",
  "$2,500–$5,000",
  "$5,000–$10,000",
  "Over $10,000",
  "Not Sure Yet",
];

const CREATIVE_STATUS_OPTIONS = [
  "I already have a finished advertisement",
  "I need help creating an advertisement",
  "I'm not sure yet",
];

const OPPORTUNITY_BULLETS = [
  "Commercial placement on the YCN app",
  "Pre-roll, mid-roll, or post-roll video advertisements",
  "Advertising during YCN original shows and content",
  "Sponsored segments and branded integrations",
  "Website banner placement",
  "Featured business or product spotlights",
  "Social media promotion",
  "Event sponsorships",
  "Custom advertising partnerships",
];

const initialForm = {
  contact_name: "",
  business_name: "",
  email: "",
  phone: "",
  website: "",
  social_handle: "",
  industry: "",
  what_to_advertise: "",
  preferred_opportunity: "",
  campaign_goals: "",
  target_audience: "",
  budget: "",
  start_date: "",
  campaign_length: "",
  creative_status: "",
  additional_details: "",
};

export default function Advertise() {
  const [form, setForm] = useState(initialForm);
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File exceeds 25 MB limit.");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please confirm consent to be contacted about advertising opportunities.");
      return;
    }
    if (!form.preferred_opportunity) {
      toast.error("Please select a preferred advertising opportunity.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      fd.append("consent", "true");
      if (file) fd.append("file", file);
      await api.post("/advertising-inquiries", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="advertise-page" className="pt-24 md:pt-28 min-h-[80vh] px-6 md:px-12 pb-24">
      <SEO
        title="Advertise With YCN"
        description="Advertise with Your Channel Network. Reach an engaged audience through commercials, video pre-roll/mid-roll placements, sponsored segments, website banners, social promotion, event sponsorships, and custom partnerships."
        path="/advertise"
      />

      {/* Hero */}
      <div className="max-w-[1200px] mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full ycn-glass text-[11px] uppercase tracking-[0.3em] text-white/70 mb-5">
          <Megaphone size={12} className="text-orange-300" /> Advertise With Us
        </div>
        <h1 className="font-display text-5xl md:text-7xl font-black leading-[0.95] tracking-tight uppercase">
          Advertise With <span className="ycn-gradient-text">YCN</span>
        </h1>
        <p className="mt-6 text-white/75 max-w-3xl mx-auto text-lg leading-relaxed">
          Connect your brand with an engaged audience through Your Channel Network. We offer advertising and promotional opportunities across the YCN streaming app, website, original shows, movies, interviews, social media content, events, and other YCN-produced programming.
        </p>
      </div>

      {/* Success state */}
      {done && (
        <div className="max-w-[820px] mx-auto mt-14 ycn-glass rounded-3xl p-10 text-center" data-testid="advertise-success">
          <div className="mx-auto h-14 w-14 rounded-full bg-gradient-to-br from-violet-500 via-blue-500 to-orange-500 flex items-center justify-center mb-5">
            <Check size={26} className="text-white" />
          </div>
          <div className="font-display text-3xl md:text-4xl font-black ycn-gradient-text">
            Inquiry Received
          </div>
          <p className="mt-4 text-white/80 text-lg leading-relaxed max-w-xl mx-auto">
            Thank you for your interest in advertising with Your Channel Network. Our team has received your inquiry and will contact you regarding available opportunities.
          </p>
          <button
            data-testid="advertise-submit-another"
            onClick={() => { setForm(initialForm); setFile(null); setConsent(false); setDone(false); }}
            className="mt-8 inline-flex items-center gap-2 px-6 py-2.5 rounded-full ycn-glass hover:bg-white/15 text-sm font-semibold transition"
          >
            Submit another inquiry
          </button>
        </div>
      )}

      {/* Spotlight + opportunity list */}
      {!done && (
        <>
          <section className="max-w-[1200px] mx-auto mt-16 md:mt-20" data-testid="advertise-spotlight">
            <div className="relative overflow-hidden rounded-3xl border border-white/10">
              <div className="absolute inset-0 bg-gradient-to-br from-[#1E1B4B] via-[#0B0B0F] to-[#7C2D12]" />
              <div className="absolute inset-0 opacity-60" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(147,51,234,0.4), transparent 55%), radial-gradient(circle at 80% 70%, rgba(249,115,22,0.4), transparent 55%)" }} />
              <div className="relative p-8 md:p-12 grid md:grid-cols-[1fr,1fr] gap-8 items-center">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.35em] text-white/70 mb-3">Partnership Opportunities</div>
                  <h2 className="font-display text-3xl md:text-4xl font-black leading-tight uppercase">
                    Put Your Brand in the <span className="ycn-gradient-text">Spotlight</span>
                  </h2>
                  <p className="mt-4 text-white/85 text-base md:text-lg leading-relaxed">
                    Businesses, brands, organizations, and entrepreneurs can inquire about a wide range of visibility opportunities across the YCN ecosystem — from streaming placements to social and event partnerships.
                  </p>
                  <p className="mt-4 text-white/70 text-sm leading-relaxed">
                    Advertising opportunities and packages may be customized based on the advertiser's goals, campaign length, placement, and audience. Share your goals below and our team will follow up with recommendations.
                  </p>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {OPPORTUNITY_BULLETS.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-white/85 bg-white/[0.05] border border-white/10 rounded-xl px-3.5 py-2.5">
                      <Sparkles size={13} className="mt-0.5 text-orange-300 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Inquiry Form */}
          <section className="max-w-[1000px] mx-auto mt-16 md:mt-20" data-testid="advertise-form-section">
            <div className="text-center mb-8">
              <div className="text-[11px] uppercase tracking-[0.35em] text-white/60 mb-2">Let's Talk</div>
              <h2 className="font-display text-3xl md:text-4xl font-black uppercase">
                Advertising & Partnership <span className="ycn-gradient-text">Inquiry</span>
              </h2>
              <p className="mt-3 text-white/60 text-sm">Fields marked with <span className="text-red-400">*</span> are required.</p>
            </div>

            <form onSubmit={submit} className="ycn-glass rounded-3xl p-6 md:p-10 space-y-5" data-testid="advertise-form">
              <div className="grid md:grid-cols-2 gap-5">
                <FormField label="Contact Name" required>
                  <input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)}
                    data-testid="adv-field-contact-name" className="ycn-input" maxLength={120} />
                </FormField>
                <FormField label="Business or Organization Name" required>
                  <input required value={form.business_name} onChange={(e) => set("business_name", e.target.value)}
                    data-testid="adv-field-business-name" className="ycn-input" maxLength={200} />
                </FormField>
                <FormField label="Email Address" required>
                  <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                    data-testid="adv-field-email" className="ycn-input" />
                </FormField>
                <FormField label="Phone Number">
                  <input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                    data-testid="adv-field-phone" className="ycn-input" maxLength={60} />
                </FormField>
                <FormField label="Business Website">
                  <input value={form.website} onChange={(e) => set("website", e.target.value)}
                    data-testid="adv-field-website" className="ycn-input" placeholder="https://…" maxLength={300} />
                </FormField>
                <FormField label="Social Media Handle">
                  <input value={form.social_handle} onChange={(e) => set("social_handle", e.target.value)}
                    data-testid="adv-field-social" className="ycn-input" placeholder="@yourbrand" maxLength={120} />
                </FormField>
                <FormField label="Industry or Business Type">
                  <input value={form.industry} onChange={(e) => set("industry", e.target.value)}
                    data-testid="adv-field-industry" className="ycn-input" maxLength={120} />
                </FormField>
                <FormField label="Preferred Advertising Opportunity" required>
                  <select required value={form.preferred_opportunity} onChange={(e) => set("preferred_opportunity", e.target.value)}
                    data-testid="adv-field-opportunity" className="ycn-input">
                    <option value="" className="bg-[#0f0f11]">— Select an option —</option>
                    {OPPORTUNITY_OPTIONS.map((o) => (
                      <option key={o} value={o} className="bg-[#0f0f11]">{o}</option>
                    ))}
                  </select>
                </FormField>
              </div>

              <FormField label="What would you like to advertise?" required>
                <textarea required rows={3} value={form.what_to_advertise} onChange={(e) => set("what_to_advertise", e.target.value)}
                  data-testid="adv-field-what" className="ycn-input" maxLength={1000}
                  placeholder="A brief description of what you'd like to promote" />
              </FormField>

              <div className="grid md:grid-cols-2 gap-5">
                <FormField label="Campaign Goals">
                  <textarea rows={3} value={form.campaign_goals} onChange={(e) => set("campaign_goals", e.target.value)}
                    data-testid="adv-field-goals" className="ycn-input" maxLength={2000} />
                </FormField>
                <FormField label="Target Audience">
                  <textarea rows={3} value={form.target_audience} onChange={(e) => set("target_audience", e.target.value)}
                    data-testid="adv-field-audience" className="ycn-input" maxLength={1000} />
                </FormField>
              </div>

              <div className="grid md:grid-cols-3 gap-5">
                <FormField label="Estimated Advertising Budget">
                  <select value={form.budget} onChange={(e) => set("budget", e.target.value)}
                    data-testid="adv-field-budget" className="ycn-input">
                    <option value="" className="bg-[#0f0f11]">— Select —</option>
                    {BUDGET_OPTIONS.map((o) => (
                      <option key={o} value={o} className="bg-[#0f0f11]">{o}</option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Preferred Campaign Start Date">
                  <input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)}
                    data-testid="adv-field-start-date" className="ycn-input" />
                </FormField>
                <FormField label="Desired Campaign Length">
                  <input value={form.campaign_length} onChange={(e) => set("campaign_length", e.target.value)}
                    data-testid="adv-field-campaign-length" className="ycn-input" placeholder="e.g. 3 months" maxLength={120} />
                </FormField>
              </div>

              {/* Creative status radio group */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2.5">
                  Do you already have a finished commercial, or need help creating one?
                </div>
                <div className="grid md:grid-cols-3 gap-2.5" role="radiogroup" aria-label="Creative status">
                  {CREATIVE_STATUS_OPTIONS.map((opt) => {
                    const active = form.creative_status === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => set("creative_status", opt)}
                        data-testid={`adv-creative-${opt.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                        className={`text-left px-4 py-3 rounded-2xl border text-sm transition ${active ? "bg-gradient-to-r from-violet-600/30 via-blue-600/30 to-orange-500/30 border-white/40 text-white font-semibold" : "bg-white/5 border-white/10 text-white/75 hover:bg-white/10"}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`h-3.5 w-3.5 rounded-full border ${active ? "bg-white border-white" : "border-white/30"}`} />
                          {opt}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* File upload */}
              <FormField label="Upload Advertisement, Media Kit, Logo, or Supporting File">
                <div className="flex flex-wrap items-center gap-3">
                  <label htmlFor="adv-file" data-testid="adv-file-label"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full ycn-glass hover:bg-white/15 text-sm cursor-pointer transition">
                    <Upload size={14} /> Choose file
                  </label>
                  <input id="adv-file" type="file" hidden onChange={onFilePick}
                    accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,video/mp4,video/quicktime"
                    data-testid="adv-file-input" />
                  {file && (
                    <div className="flex items-center gap-2 text-sm text-white/80 bg-white/5 border border-white/10 rounded-full pl-3 pr-1.5 py-1">
                      <span className="truncate max-w-[220px]" data-testid="adv-file-name">{file.name}</span>
                      <button type="button" onClick={() => setFile(null)} data-testid="adv-file-remove"
                        aria-label="Remove file"
                        className="h-6 w-6 rounded-full hover:bg-white/15 flex items-center justify-center">
                        <X size={12} />
                      </button>
                    </div>
                  )}
                  <span className="text-[11px] text-white/50">Max 25 MB · Images, PDF, DOC/DOCX, MP4, MOV</span>
                </div>
              </FormField>

              <FormField label="Additional Details or Questions">
                <textarea rows={4} value={form.additional_details} onChange={(e) => set("additional_details", e.target.value)}
                  data-testid="adv-field-additional" className="ycn-input" maxLength={4000} />
              </FormField>

              {/* Consent */}
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="adv-consent-label">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  data-testid="adv-consent-checkbox"
                  className="mt-1 h-4 w-4 rounded border-white/30 bg-white/5 accent-orange-500" />
                <span className="text-sm text-white/75 leading-relaxed">
                  I confirm that Your Channel Network may contact me regarding advertising opportunities. <span className="text-red-400">*</span>
                </span>
              </label>

              <div className="pt-2 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
                <p className="text-[11px] text-white/50 leading-relaxed">
                  We do not publish pricing publicly. Packages are customized to your goals, placement, campaign length, and audience.
                </p>
                <button type="submit" disabled={loading}
                  data-testid="adv-submit-btn"
                  className="ycn-btn-primary inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full font-bold text-white transition-all disabled:opacity-60">
                  <Send size={15} /> {loading ? "Submitting…" : "Submit Inquiry"}
                </button>
              </div>
            </form>
          </section>
        </>
      )}
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.25em] text-white/60 mb-1.5">
        {label}{required && <span className="text-red-400"> *</span>}
      </span>
      {children}
    </label>
  );
}
